import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import PublishChangesButton from "@/components/PublishChangesButton";

/**
 * AdminSbtServiceKB — CRUD editor for the 27-entry Service Knowledge
 * Base powering /services/[slug]/ detail pages and (eventually) the
 * WhatsApp AI Concierge.
 *
 * Each entry has:
 *   • name, description (60-100 word marketing writeup)
 *   • hero_image { url, credit_name, credit_profile, credit_unsplash, alt }
 *   • key_benefits[], who_its_for, common_questions[{q,a}]
 *   • pricing_signal, case_snapshot, related_services[]
 *
 * Uses:
 *   GET  /api/admin/sbt/service-kb              → list all
 *   PUT  /api/admin/sbt/service-kb/{slug}       → save one
 *   POST /api/admin/sbt/service-kb/reseed       → reset from JSON file
 */
const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  solutions: "AI Powered Solutions",
  product: "AI Powered Product",
  data: "Data Signals",
  challenges: "What We Solve",
};

export default function AdminSbtServiceKB() {
  const [items, setItems] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/admin/sbt/service-kb`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setItems(j.items || []);
      if (!selectedSlug && (j.items || []).length) {
        setSelectedSlug(j.items[0].slug);
      }
    } catch (e) {
      toast.error("Failed to load Service KB: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selectedSlug) return;
    const entry = items.find((x) => x.slug === selectedSlug);
    if (entry) {
      // Deep-clone so edits don't mutate the list.
      setDraft(JSON.parse(JSON.stringify(entry)));
    }
    // Deliberately DO NOT depend on `items` here — otherwise the sequence
    //   type → click Save → items mutates → this effect refires → setDraft(entry)
    // clobbers any keystrokes the user typed between "click Save" and
    // "PUT response arrived". Post-save refresh is handled explicitly by
    // save() below.
  }, [selectedSlug]);

  // First-time hydration: when the initial GET of items resolves AFTER
  // `selectedSlug` was set, do a one-shot hydration so draft gets
  // populated. `draft` staying non-null after that gates future re-hydration.
  useEffect(() => {
    if (draft || !selectedSlug || items.length === 0) return;
    const entry = items.find((x) => x.slug === selectedSlug);
    if (entry) setDraft(JSON.parse(JSON.stringify(entry)));
  }, [items, selectedSlug, draft]);

  const grouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const src = q
      ? items.filter((x) => (x.name || "").toLowerCase().includes(q) || (x.slug || "").includes(q))
      : items;
    const out = { solutions: [], product: [], data: [], challenges: [] };
    for (const it of src) (out[it.category] || out.solutions).push(it);
    return out;
  }, [items, filter]);

  const wordCount = (draft?.description || "").trim().split(/\s+/).filter(Boolean).length;

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/admin/sbt/service-kb/${draft.slug}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(draft),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const saved = await r.json();
      setItems((prev) => prev.map((x) => (x.slug === saved.slug ? saved : x)));
      // Explicitly refresh the draft from the server response so what the
      // user sees is exactly what was persisted (including any backend
      // normalisation of key_benefits / faq_items). We do this HERE rather
      // than relying on a useEffect on [items] to avoid clobbering
      // in-flight edits during the PUT roundtrip.
      setDraft(JSON.parse(JSON.stringify(saved)));
      toast.success(`Saved: ${saved.name}. Publish Changes to update the live site.`);
    } catch (e) {
      toast.error("Save failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const reseed = async () => {
    if (!window.confirm("Reseed the Service KB from service_kb.json? This will DISCARD any edits you have made.")) return;
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/admin/sbt/service-kb/reseed`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      toast.success(`Reseeded ${j.reseeded} services`);
      load();
    } catch (e) {
      toast.error("Reseed failed: " + e.message);
    }
  };

  const patch = (obj) => setDraft((d) => ({ ...d, ...obj }));
  const patchHero = (obj) => setDraft((d) => ({ ...d, hero_image: { ...(d.hero_image || {}), ...obj } }));

  // ────────── Write with AI ──────────
  const [aiState, setAiState] = useState({ field: null, busy: false });
  const callAI = async ({ field, mode, current = "", hint = "", extra = {} }) => {
    if (!draft) return null;
    setAiState({ field, busy: true });
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/admin/sbt/service-kb/${draft.slug}/ai-write`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ field, mode, current, hint, extra }),
      });
      if (!r.ok) {
        const err = await r.text();
        throw new Error(err.slice(0, 200));
      }
      const j = await r.json();
      return j;
    } catch (e) {
      toast.error("AI write failed: " + e.message);
      return null;
    } finally {
      setAiState({ field: null, busy: false });
    }
  };

  const aiWriteDescription = async (mode) => {
    const hint = mode === "write" ? "" : (window.prompt("Any specific angle? (optional)", "") || "");
    const j = await callAI({ field: "description", mode, current: draft.description || "", hint });
    if (j?.text) {
      patch({ description: j.text });
      toast.success(`AI ${mode} — description updated`);
    }
  };
  const aiWriteWhoFor = async () => {
    const j = await callAI({ field: "who_its_for", mode: "write", current: draft.who_its_for || "" });
    if (j?.text) { patch({ who_its_for: j.text }); toast.success("AI wrote 'Who it's for'"); }
  };
  const aiWritePricing = async () => {
    const j = await callAI({ field: "pricing_signal", mode: "write", current: draft.pricing_signal || "" });
    if (j?.text) { patch({ pricing_signal: j.text }); toast.success("AI wrote pricing signal"); }
  };
  const aiWriteCase = async () => {
    const j = await callAI({ field: "case_snapshot", mode: "write", current: draft.case_snapshot || "" });
    if (j?.text) { patch({ case_snapshot: j.text }); toast.success("AI wrote case snapshot"); }
  };
  const aiWriteBenefits = async () => {
    if ((draft.key_benefits || []).length && !window.confirm(`Replace all ${draft.key_benefits.length} benefits with AI-generated ones?`)) return;
    const j = await callAI({ field: "key_benefits", mode: "write" });
    if (j?.alternatives?.length) { patch({ key_benefits: j.alternatives }); toast.success(`AI wrote ${j.alternatives.length} benefits`); }
  };
  const aiWriteFAQPair = async () => {
    const j = await callAI({ field: "faq_pair", mode: "write" });
    const first = (j?.alternatives || [])[0];
    if (first?.q && first?.a) {
      patch({ common_questions: [...(draft.common_questions || []), first] });
      toast.success("AI wrote a new Q&A");
    }
  };
  const aiWriteFAQAnswer = async (idx) => {
    const q = draft.common_questions?.[idx]?.q || "";
    if (!q) { toast.error("Add a question first, then click ✨ to draft the answer."); return; }
    const j = await callAI({ field: "faq_answer", mode: "write", extra: { question: q } });
    if (j?.text) { updateQA(idx, "a", j.text); toast.success("AI wrote FAQ answer"); }
  };

  const AIBtn = useCallback(
    (props) => <AIBtnBase {...props} aiState={aiState} />,
    [aiState]
  );

  const updateBenefit = (i, val) => {
    const arr = [...(draft.key_benefits || [])]; arr[i] = val; patch({ key_benefits: arr });
  };
  const addBenefit = () => patch({ key_benefits: [...(draft.key_benefits || []), ""] });
  const removeBenefit = (i) => patch({ key_benefits: (draft.key_benefits || []).filter((_, idx) => idx !== i) });

  const updateQA = (i, field, val) => {
    const arr = [...(draft.common_questions || [])];
    arr[i] = { ...arr[i], [field]: val };
    patch({ common_questions: arr });
  };
  const addQA = () => patch({ common_questions: [...(draft.common_questions || []), { q: "", a: "" }] });
  const removeQA = (i) => patch({ common_questions: (draft.common_questions || []).filter((_, idx) => idx !== i) });

  if (loading) return <div className="p-6 text-slate-500 text-sm">Loading Service KB…</div>;

  return (
    <div className="grid grid-cols-[280px_1fr] gap-6" data-testid="admin-svc-kb">
      {/* Left rail — 27 service list */}
      <aside className="border rounded-lg bg-white overflow-hidden max-h-[75vh] flex flex-col">
        <div className="p-3 border-b bg-slate-50 sticky top-0">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={`Filter ${items.length} services…`}
            className="w-full text-sm px-3 py-1.5 border border-slate-200 rounded focus:outline-none focus:border-cyan-500"
            data-testid="svc-kb-filter"
          />
        </div>
        <div className="overflow-y-auto flex-1">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50 px-3 py-1.5 border-b">
                {CATEGORY_LABELS[cat]} · {list.length}
              </div>
              {list.map((it) => {
                const active = it.slug === selectedSlug;
                const hasImg = !!(it.hero_image && it.hero_image.url);
                return (
                  <button
                    key={it.slug}
                    onClick={() => setSelectedSlug(it.slug)}
                    className={
                      "w-full text-left px-3 py-2 border-b border-slate-100 text-sm flex items-center gap-2 transition-colors " +
                      (active ? "bg-cyan-50 text-cyan-800 font-semibold" : "hover:bg-slate-50")
                    }
                    data-testid={`svc-kb-pick-${it.slug}`}
                  >
                    <span className={"inline-block w-1.5 h-1.5 rounded-full " + (hasImg ? "bg-emerald-500" : "bg-slate-300")}
                          title={hasImg ? "Hero image set" : "No hero image"}/>
                    <span className="truncate flex-1">{it.name}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="p-3 border-t bg-slate-50 text-xs text-slate-500 space-y-2">
          <div>{items.length} services · {items.filter(x => x.hero_image?.url).length} with hero image</div>
          <button
            onClick={reseed}
            className="w-full text-[11px] text-slate-600 hover:text-red-600 underline underline-offset-2"
            data-testid="svc-kb-reseed"
          >
            Reset from JSON file
          </button>
        </div>
      </aside>

      {/* Right — editor */}
      {draft ? (
        <div className="border rounded-lg bg-white p-6 space-y-6 overflow-y-auto max-h-[75vh]" data-testid="svc-kb-editor">
          <header className="flex items-start justify-between gap-3 pb-3 border-b">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-cyan-600">{CATEGORY_LABELS[draft.category]}</div>
              <h3 className="text-lg font-bold text-slate-900 mt-0.5">{draft.name}</h3>
              <div className="text-xs text-slate-500 mt-1 font-mono">/services/{draft.slug}/</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={save}
                disabled={saving}
                className="px-5 py-2 bg-cyan-600 text-white text-sm font-semibold rounded hover:bg-cyan-700 disabled:opacity-50"
                data-testid="svc-kb-save"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              {/* One-click publish — rebuilds the Astro site so every Save
                  to Mongo shows up on /services/{slug}/ within ~15s. */}
              <PublishChangesButton />
            </div>
          </header>

          {/* Display name */}
          <Field label="Display name">
            <input
              type="text" value={draft.name || ""}
              onChange={(e) => patch({ name: e.target.value })}
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded focus:outline-none focus:border-cyan-500"
            />
          </Field>

          {/* Description — main writeup */}
          <Field
            label="Marketing description (target 60–100 words)"
            hint={<span className={wordCount < 55 || wordCount > 130 ? "text-red-600" : "text-emerald-600"}>{wordCount} words</span>}
            action={
              <div className="flex gap-1.5">
                <AIBtn label="Write" field="description-write" onClick={() => aiWriteDescription("write")} />
                <AIBtn label="Improve" field="description-improve" onClick={() => aiWriteDescription("improve")} />
                <AIBtn label="Shorten" field="description-shorten" onClick={() => aiWriteDescription("shorten")} />
              </div>
            }
          >
            <textarea
              rows={7} value={draft.description || ""}
              onChange={(e) => patch({ description: e.target.value })}
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded focus:outline-none focus:border-cyan-500 leading-relaxed"
              data-testid="svc-kb-desc"
            />
          </Field>

          {/* Hero image */}
          <fieldset className="space-y-3 p-4 border border-slate-200 rounded-lg bg-slate-50/50">
            <legend className="text-xs font-bold uppercase tracking-widest text-slate-600 px-2">Hero photo</legend>
            {draft.hero_image?.url && (
              <img src={draft.hero_image.url} alt={draft.hero_image.alt || ""}
                   className="w-full max-h-40 object-cover rounded border border-slate-200"/>
            )}
            <Field label="Image URL">
              <input type="text" value={draft.hero_image?.url || ""}
                     onChange={(e) => patchHero({ url: e.target.value })}
                     placeholder="https://images.unsplash.com/…"
                     className="w-full text-xs px-3 py-2 border border-slate-200 rounded font-mono focus:outline-none focus:border-cyan-500"
                     data-testid="svc-kb-hero-url"/>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Alt text (accessibility)">
                <input type="text" value={draft.hero_image?.alt || ""}
                       onChange={(e) => patchHero({ alt: e.target.value })}
                       className="w-full text-sm px-3 py-2 border border-slate-200 rounded"/>
              </Field>
              <Field label="Image search query (for refetch)">
                <input type="text" value={draft.image_query || ""}
                       onChange={(e) => patch({ image_query: e.target.value })}
                       className="w-full text-sm px-3 py-2 border border-slate-200 rounded"/>
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Credit — photographer">
                <input type="text" value={draft.hero_image?.credit_name || ""}
                       onChange={(e) => patchHero({ credit_name: e.target.value })}
                       className="w-full text-xs px-3 py-2 border border-slate-200 rounded"/>
              </Field>
              <Field label="Credit — profile URL">
                <input type="text" value={draft.hero_image?.credit_profile || ""}
                       onChange={(e) => patchHero({ credit_profile: e.target.value })}
                       className="w-full text-xs px-3 py-2 border border-slate-200 rounded font-mono"/>
              </Field>
              <Field label="Credit — Unsplash URL">
                <input type="text" value={draft.hero_image?.credit_unsplash || ""}
                       onChange={(e) => patchHero({ credit_unsplash: e.target.value })}
                       className="w-full text-xs px-3 py-2 border border-slate-200 rounded font-mono"/>
              </Field>
            </div>
          </fieldset>

          {/* Who it's for */}
          <Field label="Who it's for (1 sentence)"
                 action={<AIBtn label="Write with AI" field="who_its_for" onClick={aiWriteWhoFor} />}>
            <input type="text" value={draft.who_its_for || ""}
                   onChange={(e) => patch({ who_its_for: e.target.value })}
                   className="w-full text-sm px-3 py-2 border border-slate-200 rounded"/>
          </Field>

          {/* Pricing signal */}
          <Field label="Pricing signal (shown next to hero CTA)"
                 action={<AIBtn label="Write with AI" field="pricing_signal" onClick={aiWritePricing} />}>
            <input type="text" value={draft.pricing_signal || ""}
                   onChange={(e) => patch({ pricing_signal: e.target.value })}
                   placeholder="e.g. Custom — typically $12-40k/month"
                   className="w-full text-sm px-3 py-2 border border-slate-200 rounded"/>
          </Field>

          {/* Key benefits array */}
          <fieldset className="space-y-2 p-4 border border-slate-200 rounded-lg">
            <legend className="text-xs font-bold uppercase tracking-widest text-slate-600 px-2 flex items-center gap-2">
              Key benefits
              <AIBtn label="Regenerate all with AI" field="key_benefits" onClick={aiWriteBenefits} />
            </legend>
            {(draft.key_benefits || []).map((b, i) => (
              <div key={i} className="flex gap-2">
                <input type="text" value={b}
                       onChange={(e) => updateBenefit(i, e.target.value)}
                       className="flex-1 text-sm px-3 py-1.5 border border-slate-200 rounded"/>
                <button onClick={() => removeBenefit(i)} className="text-xs text-red-600 px-2">Remove</button>
              </div>
            ))}
            <button onClick={addBenefit} className="text-xs text-cyan-700 font-semibold" data-testid="svc-kb-add-benefit">+ Add benefit</button>
          </fieldset>

          {/* Common questions */}
          <fieldset className="space-y-3 p-4 border border-slate-200 rounded-lg">
            <legend className="text-xs font-bold uppercase tracking-widest text-slate-600 px-2 flex items-center gap-2">
              Common questions (FAQ)
              <AIBtn label="Suggest new Q&A" field="faq_pair" onClick={aiWriteFAQPair} />
            </legend>
            {(draft.common_questions || []).map((qa, i) => (
              <div key={i} className="space-y-2 p-3 bg-slate-50 rounded border border-slate-100">
                <input type="text" value={qa.q || ""}
                       placeholder="Question"
                       onChange={(e) => updateQA(i, "q", e.target.value)}
                       className="w-full text-sm px-3 py-1.5 border border-slate-200 rounded font-semibold"/>
                <div className="relative">
                  <textarea rows={3} value={qa.a || ""}
                            placeholder="Answer (analyst-informed, 1-3 sentences)"
                            onChange={(e) => updateQA(i, "a", e.target.value)}
                            className="w-full text-sm px-3 py-1.5 pr-32 border border-slate-200 rounded"/>
                  <div className="absolute top-1.5 right-1.5">
                    <AIBtn label="Draft answer" field={`faq-answer-${i}`} onClick={() => aiWriteFAQAnswer(i)} />
                  </div>
                </div>
                <button onClick={() => removeQA(i)} className="text-xs text-red-600">Remove Q&A</button>
              </div>
            ))}
            <button onClick={addQA} className="text-xs text-cyan-700 font-semibold" data-testid="svc-kb-add-qa">+ Add question</button>
          </fieldset>

          {/* Case snapshot */}
          <Field label="Client case snapshot (1-2 sentences)"
                 action={<AIBtn label="Write with AI" field="case_snapshot" onClick={aiWriteCase} />}>
            <textarea rows={2} value={draft.case_snapshot || ""}
                      onChange={(e) => patch({ case_snapshot: e.target.value })}
                      className="w-full text-sm px-3 py-2 border border-slate-200 rounded"/>
          </Field>

          {/* Related services */}
          <Field label="Related service slugs (comma-separated)">
            <input type="text"
                   value={(draft.related_services || []).join(", ")}
                   onChange={(e) => patch({ related_services: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                   placeholder="e.g. abm, buying-group, intent-first"
                   className="w-full text-sm px-3 py-2 border border-slate-200 rounded font-mono"/>
          </Field>

          <div className="pt-4 border-t flex items-center justify-between">
            <a href={`/sbt-site/services/${draft.slug}/`} target="_blank" rel="noopener noreferrer"
               className="text-xs text-cyan-700 underline underline-offset-2"
               data-testid="svc-kb-preview">
              Preview /services/{draft.slug}/ →
            </a>
            <button onClick={save} disabled={saving}
                    className="px-5 py-2 bg-cyan-600 text-white text-sm font-semibold rounded hover:bg-cyan-700 disabled:opacity-50">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      ) : (
        <div className="text-slate-400 text-sm p-8">Select a service on the left to start editing.</div>
      )}
    </div>
  );
}

function Field({ label, hint, action, children }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5 gap-3 flex-wrap">
        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</span>
        <div className="flex items-center gap-2">
          {hint && <span className="text-[11px] font-mono">{hint}</span>}
          {action}
        </div>
      </div>
      {children}
    </label>
  );
}

function AIBtnBase({ label = "Write with AI", onClick, field, className = "", aiState }) {
  const busy = aiState?.busy && aiState?.field === field;
  return (
    <button
      type="button" onClick={onClick}
      disabled={aiState?.busy}
      className={"inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-full border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 hover:border-amber-400 disabled:opacity-50 transition " + className}
      data-testid={`ai-write-${field}`}
      title="AI-generate this field using Claude Sonnet"
    >
      <Sparkles size={12} className={busy ? "animate-spin" : ""} />
      {busy ? "Writing…" : label}
    </button>
  );
}
